import { debounce } from 'lodash';
import { Box, Input, InputGroup, InputLeftElement, Text } from '@chakra-ui/react';
import { useState, useMemo, useEffect } from 'react';
import { Search2Icon } from '@chakra-ui/icons';
import { formatCurrency } from '@helpers/utils';

const InputComp = ({
    label = 'Label',
    placeholder = 'Placeholder...',
    value = null,
    onChange = () => {},
    helperText = '',
    isInvalid = false,
    debounceDelay = 500,
    isDisabled = false,
    isReadOnly = false,
    isShowLabel = true,
    Icon = Search2Icon,
    width = null,
    isRightElement = false,
    isDimBg = false,
    isRequired = false,
    extraStyle,
    type = 'text',
    isRupiah = false,
}) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        if (isRupiah) {
            setLocalValue(value ? formatCurrency(value) : value);
        } else {
            setLocalValue(value);
        }
    }, [value, isRupiah]);

    const debouncedOnChange = useMemo(
        () =>
            debounce((val) => {
                onChange(val);
            }, debounceDelay),
        [onChange, debounceDelay],
    );

    useEffect(() => {
        return () => {
            debouncedOnChange.cancel();
        };
    }, [debouncedOnChange]);

    const handleChange = (e) => {
        let val = e.target.value;

        if (isRupiah) {
            const rawValue = val.replace(/\D/g, '');
            setLocalValue(formatCurrency(rawValue));
            debouncedOnChange(rawValue);
            return;
        }

        if (type === 'number') {
            val = val.replace(/\D/g, '');
        }

        setLocalValue(val);
        debouncedOnChange(val);
    };

    const bg = isDimBg ? 'semantic.200' : 'semantic.100'; // Change this inside InputComp:

    return (
        <Box style={{ ...extraStyle }}>
            {isShowLabel && (
                <Text fontSize="14px" fontWeight="700" mb="8px" color={isDisabled ? 'neutral.400' : 'semantic.500'}>
                    {label}{' '}
                    {isRequired && (
                        <Box as="span" color="red">
                            *
                        </Box>
                    )}
                </Text>
            )}

            <InputGroup w={width ?? 'full'}>
                {isRightElement && (
                    <InputLeftElement pointerEvents="none">
                        <Icon color="gray.500" />
                    </InputLeftElement>
                )}

                <Input
                    type={type}
                    value={localValue}
                    onChange={handleChange}
                    placeholder={placeholder}
                    fontSize="14px"
                    isInvalid={isInvalid}
                    disabled={isDisabled}
                    readOnly={isReadOnly}
                    bg={bg}
                    required={isRequired}
                    _invalid={{
                        borderColor: 'danger.500',
                    }}
                />
            </InputGroup>

            {helperText && (
                <Text fontSize="10px" mt="8px" color={isInvalid ? 'danger.500' : 'semantic.500'}>
                    {helperText}
                </Text>
            )}
        </Box>
    );
};

export default InputComp;
